import { useState, type FormEvent } from 'react';
import { Button, TextField } from '../charte';
import { setPassword } from '../../api/candidate';
import { newPasswordProblem } from '../../lib/password';
import { initials } from '../../lib/dates';
import { MyPhotoEditor } from '../Photos';
import type { CandidateProfile } from '../../types';
import { Sheet } from './Sheet';

export function ProfileSheet({ candidate, onClose }: { candidate: CandidateProfile; onClose: () => void }) {
  const rows: { label: string; value: string | null | undefined }[] = [
    { label: 'Nom', value: `${candidate.firstName} ${candidate.lastName}`.trim() },
    { label: 'Email', value: candidate.email },
    { label: 'Téléphone', value: candidate.phoneNumber },
    { label: 'Catégorie de permis', value: candidate.vehicleType },
    { label: 'Auto-école', value: candidate.school?.name },
  ];
  return (
    <Sheet title="Mon profil" onClose={onClose} footer={<Button className="w-full" onClick={onClose}>Fermer</Button>}>
      <MyPhotoEditor initials={initials(candidate.firstName, candidate.lastName)} />
      <dl className="mt-2 divide-y divide-slate-100 rounded-row border border-line px-4">
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between gap-4 py-3">
            <dt className="shrink-0 text-[13px] text-secondary">{r.label}</dt>
            <dd className="min-w-0 truncate text-right text-sm font-medium text-heading">{r.value || '—'}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-3 text-xs text-muted">Pour modifier ces informations, contactez votre auto-école.</p>
    </Sheet>
  );
}

export function PasswordSheet({ hasPassword, onClose, onSaved }: { hasPassword: boolean; onClose: () => void; onSaved: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const problem = newPasswordProblem(next, confirm);
    setError(problem);
    if (problem) return;
    setBusy(true);
    try {
      await setPassword(next, hasPassword ? current : undefined);
      onSaved();
    } catch (err: unknown) {
      const msg = err && typeof err === 'object' && 'message' in err ? String((err as { message?: unknown }).message) : 'Échec de la mise à jour.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      title={hasPassword ? 'Changer mon mot de passe' : 'Définir un mot de passe'}
      description="Vous pourrez ensuite vous connecter avec votre email et ce mot de passe."
      onClose={onClose}
    >
      <form onSubmit={submit} className="grid gap-4">
        {hasPassword && (
          <TextField label="Mot de passe actuel" type="password" autoComplete="current-password" required value={current} onChange={(e) => setCurrent(e.target.value)} />
        )}
        <TextField
          label="Nouveau mot de passe"
          hint="8 caractères minimum."
          type="password"
          autoComplete="new-password"
          required
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <TextField label="Confirmer le mot de passe" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        {error && (
          <p className="rounded-xl bg-danger-light px-3 py-2 text-sm text-danger-dark" role="alert">
            {error}
          </p>
        )}
        <div className="flex gap-2 pt-1">
          <Button className="flex-1" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" className="flex-1" loading={busy}>
            Enregistrer
          </Button>
        </div>
      </form>
    </Sheet>
  );
}
